import { useState } from 'react';
import { observer } from 'mobx-react-lite';
import { Copy, Check, ShieldCheck, ShieldAlert, Heart } from 'lucide-react';
import toast from 'react-hot-toast';
import { cn } from '@/utils/cn';
import { ServerIcon, GameGlyph, useServerIconUrl } from '../ServerIcon';
import { PlayerAvatar } from '../PlayerAvatar';
import { MotdText } from './MotdText';
import { formatAgo, formatUptime } from '../../../utils/overview-format';
import { softwareParts } from '../../../utils/mc-labels';
import type { Server } from '../../../types';
import type { McOverview } from './types';

const HERO_SHADE =
  'linear-gradient(90deg, rgba(11,11,16,0.97) 0%, rgba(11,11,16,0.9) 40%, rgba(11,11,16,0.35) 72%, rgba(11,11,16,0.5) 100%), linear-gradient(0deg, rgba(11,11,16,0.7) 0%, transparent 45%)';
// Das Icon-Motiv rechts läuft nach links weich aus.
const ART_MASK = 'linear-gradient(90deg, transparent 0%, #000 42%)';
const DOTS = 'radial-gradient(rgba(255,255,255,0.08) 1px, transparent 1px)';

const TONE = {
  online: { dot: 'bg-online', ring: 'shadow-[0_0_0_4px_rgba(55,217,160,0.15)]' },
  warn: { dot: 'bg-warn', ring: 'shadow-[0_0_0_4px_rgba(245,184,65,0.15)]' },
  muted: { dot: 'bg-fg-muted', ring: '' },
};

interface Props {
  overview: McOverview | null;
  server: Server;
  firewall: any;
  playerNames: string[];
  maxPlayers?: number | null;
}

/**
 * „Bühne" der Minecraft-Übersicht (gleiche Sprache wie die Steam-Übersicht, Entwurf A):
 * ist der Server bereit, wie komme ich rein, wer ist online. Statt eines Spielbilds
 * leuchtet das Server-Icon unscharf im Hintergrund – jeder Server bekommt so seine Farbe.
 */
export const McHero = observer(({ overview, server, firewall, playerNames, maxPlayers }: Props) => {
  const [copied, setCopied] = useState(false);
  const { iconUrl, status: iconStatus } = useServerIconUrl(server.id);
  // Kleine Pixel-Icons (server-icon.png = 64x64) pixelig-scharf hochskalieren statt verschwommen.
  const [pixelArt, setPixelArt] = useState(false);
  const status = server.status;
  const online = status === 'online';
  const bedrock = overview?.kind === 'bedrock' || server.type === 'bedrock';
  const host = server.custom_domain || window.location.hostname;
  // Java: IMMER mit Port (so fügen Freunde die Adresse ein). Bedrock hat getrennte
  // Felder für Adresse und Port - dort nie „host:port" anzeigen.
  const address = bedrock ? host : `${host}:${server.port}`;
  const saved = formatAgo(overview?.world.savedAt);
  const mods = overview?.content?.kind === 'mods' ? overview.content.count : 0;

  const state =
    status === 'stopping'
      ? { label: 'Server stoppt …', tone: 'warn' as const, sub: 'Die Welt wird gespeichert, gleich ist er aus.' }
      : status === 'starting' || status === 'restarting' || (online && overview?.ready === 'waiting')
        ? {
            label: 'Server startet …',
            tone: 'warn' as const,
            sub: [
              mods > 50 ? `Die Welt wird geladen – bei ${mods} Mods dauert das ein paar Minuten` : 'Die Welt wird geladen',
              online && server.started_at && `seit ${formatUptime(server.started_at)}`,
            ].filter(Boolean).join(' · '),
          }
        : online
          ? {
              label: overview?.ready === 'ready' ? 'Bereit zum Spielen' : 'Server läuft',
              tone: 'online' as const,
              sub: [`Online seit ${formatUptime(server.started_at)}`, saved && `Welt zuletzt gespeichert ${saved}`].filter(Boolean).join(' · '),
            }
          : {
              label: 'Server ist gestoppt',
              tone: 'muted' as const,
              sub: ['Starte ihn oben rechts — hier steht dann, sobald er bereit ist', saved && `zuletzt gespeichert ${saved}`].filter(Boolean).join(' · '),
            };

  const count = playerNames.length;
  const max = maxPlayers ?? overview?.maxPlayers ?? null;
  const parts = softwareParts(server, overview?.software);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(address);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      toast.error('Kopieren nicht möglich — Adresse manuell markieren.');
    }
  };

  return (
    <div className="relative rounded-2xl overflow-hidden border border-line bg-surface">
      {/* Motiv: das Server-Icon groß und scharf rechts (wie das Spielbild bei Steam), dahinter nur ein leichter Farbschein. */}
      <div aria-hidden className="absolute inset-y-0 right-0 w-[58%] overflow-hidden pointer-events-none" style={{ maskImage: ART_MASK, WebkitMaskImage: ART_MASK }}>
        {iconUrl ? (
          <>
            <img src={iconUrl} alt="" className="absolute inset-0 w-full h-full object-cover scale-125 blur-2xl opacity-25 saturate-150" />
            <img
              src={iconUrl}
              alt=""
              onLoad={(e) => setPixelArt(e.currentTarget.naturalWidth <= 128)}
              className="absolute right-[7%] top-1/2 h-[112%] aspect-square object-cover rounded-[28px] opacity-60 -translate-y-1/2 -rotate-6 shadow-2xl shadow-black/60"
              style={{ imageRendering: pixelArt ? 'pixelated' : 'auto' }}
            />
          </>
        ) : iconStatus === 'error' ? (
          <div className="absolute right-[7%] top-1/2 -translate-y-1/2 -rotate-6 opacity-45">
            <GameGlyph type={server.type} size={300} className="rounded-[28px]" />
          </div>
        ) : null}
      </div>
      <div aria-hidden className="absolute inset-0" style={{ backgroundImage: DOTS, backgroundSize: '18px 18px' }} />
      <div aria-hidden className="absolute inset-0" style={{ background: HERO_SHADE }} />
      <div className="absolute inset-x-0 top-0 h-px gm-powerline" />

      <div className="relative flex flex-col md:flex-row justify-between gap-6 px-6 py-6 md:px-8 md:py-7 min-h-[272px]">
        <div className="flex flex-col justify-between gap-6 min-w-0 max-w-[680px]">
          <div className="min-w-0">
            <div className="flex items-center gap-2.5 text-[11px] uppercase tracking-[0.08em] font-medium text-fg-muted min-w-0">
              <span className="truncate" title={parts.join(' · ')}>{parts.join(' · ')}</span>
              {overview?.hardcore && (
                <span className="shrink-0 inline-flex items-center gap-1 normal-case tracking-normal text-xs text-red-400">
                  <Heart size={12} fill="currentColor" /> Hardcore
                </span>
              )}
            </div>
            <div className="flex items-center gap-4 mt-4">
              <ServerIcon serverId={server.id} type={server.type} size={56} className="rounded-xl shadow-lg shadow-black/40 ring-1 ring-white/10 shrink-0" />
              <div className="min-w-0">
                <div className="flex items-center gap-3">
                  <span className={cn('w-2.5 h-2.5 rounded-full shrink-0', TONE[state.tone].dot, TONE[state.tone].ring, state.tone !== 'muted' && 'gm-live-dot')} />
                  <h2 className="font-display text-3xl md:text-[34px] font-semibold tracking-tight leading-tight text-fg">{state.label}</h2>
                </div>
                <p className="mt-1.5 text-sm text-[#B4B4C2]">{state.sub}</p>
              </div>
            </div>
            {overview?.motd && (
              <div className="mt-4 flex items-baseline gap-2.5 min-w-0">
                <span className="text-[11px] uppercase tracking-[0.08em] font-medium text-fg-muted shrink-0">Serverliste</span>
                <MotdText text={overview.motd} className="font-mono text-[13px] truncate" />
              </div>
            )}
          </div>

          <div className="flex flex-col gap-2">
            <div className="text-[11px] uppercase tracking-[0.08em] font-medium text-fg-muted">Beitreten</div>
            <div className="flex flex-wrap items-center gap-2">
              <code className="px-3.5 py-2 rounded-[10px] font-mono text-[15px] text-fg bg-ink/70 border border-line-strong select-all">{address}</code>
              <button
                onClick={copy}
                title="Adresse kopieren"
                className="w-[38px] h-[38px] rounded-[10px] flex items-center justify-center bg-ink/70 border border-line-strong text-fg hover:text-ember transition-colors"
              >
                {copied ? <Check size={16} className="text-online" /> : <Copy size={16} />}
              </button>
              {bedrock && (
                <span className="px-2.5 py-1 rounded-full text-xs font-mono text-fg bg-ink/70 border border-line-strong">Port {server.port}</span>
              )}
              {firewall?.checked && (firewall.allOpen ? (
                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs text-online bg-online/10 border border-online/25">
                  <ShieldCheck size={13} /> Ports freigegeben
                </span>
              ) : (
                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs text-warn bg-warn/10 border border-warn/25">
                  <ShieldAlert size={13} /> Ports fehlen — siehe unten
                </span>
              ))}
            </div>
            <p className="text-xs text-fg-muted">
              {bedrock
                ? 'In Minecraft unter Spielen → Server → Server hinzufügen: Adresse und Port eintragen.'
                : 'In Minecraft unter Multiplayer → Server hinzufügen die Adresse einfügen.'}
            </p>
          </div>
        </div>

        {online && (
          <div className="md:self-end flex md:flex-col items-center md:items-end gap-2.5 shrink-0">
            {count > 0 && (
              <div className="flex">
                {playerNames.slice(0, 5).map((n, i) => (
                  <span key={n} title={n} className={cn('rounded-md ring-2 ring-ink', i > 0 && '-ml-2')}>
                    <PlayerAvatar name={n} size={32} steam={bedrock} />
                  </span>
                ))}
              </div>
            )}
            <div className="font-mono text-[40px] leading-none tracking-tight text-fg">
              {count}
              {max && <span className="text-[22px] text-fg-muted"> / {max}</span>}
            </div>
            <div className="text-[11px] uppercase tracking-[0.08em] font-medium text-fg-muted">Spieler online</div>
          </div>
        )}
      </div>
    </div>
  );
});
